import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { mapApiCustomersToCustomerRows } from "design-components";
import { customersListInitialState } from "./customers.models";
import { fetchCustomerForEdit, fetchCustomersList, inviteCustomer, updateCustomerFromList } from "./customers.thunk";

const customersListSlice = createSlice({
  name: "customersList",
  initialState: customersListInitialState,
  reducers: {
    setCustomersListSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
    },
    setCustomersListLocationFilters: (state, action: PayloadAction<string[]>) => {
      state.locationFilterIds = [...action.payload];
    },
    setCustomersListSort: (
      state,
      action: PayloadAction<{ sortingOrder: "asc" | "desc"; sortingField?: string }>,
    ) => {
      state.sortingOrder = action.payload.sortingOrder;
      if (action.payload.sortingField) {
        state.sortingField = action.payload.sortingField;
      }
    },
    setCustomersListRange: (state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) => {
      state.fromIndex = action.payload.fromIndex;
      state.toIndex = action.payload.toIndex;
    },
    setCustomersListFilters: (state, action: PayloadAction<{ listFilterStatus: string[] }>) => {
      state.listFilterStatus = [...action.payload.listFilterStatus];
    },
    clearCustomersListFilters: (state) => {
      state.listFilterStatus = [];
    },
    clearCustomersListDeleteState: (state) => {
      state.deleteSubmitting = false;
      state.deleteError = null;
    },
    setCustomersListShowOnlyActive: (state, action: PayloadAction<boolean>) => {
      state.showOnlyActive = action.payload;
    },
    setCustomersListOperatorFilters: (state, action: PayloadAction<string[]>) => {
      state.operatorFilterIds = [...action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomersList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomersList.fulfilled, (state, action) => {
        state.loading = false;
        state.totalCustomers = action.payload.data.totalCustomers;
        state.sortingField = action.payload.data.sortingField;
        state.sortingOrder = action.payload.data.sortingOrder;
        state.rows = mapApiCustomersToCustomerRows(action.payload.data.customers);
      })
      .addCase(fetchCustomersList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to fetch customers";
      })
      .addCase(inviteCustomer.pending, (state) => {
        state.formSubmitting = true;
      })
      .addCase(inviteCustomer.fulfilled, (state) => {
        state.formSubmitting = false;
      })
      .addCase(inviteCustomer.rejected, (state) => {
        state.formSubmitting = false;
      })
      .addCase(updateCustomerFromList.pending, (state) => {
        state.formSubmitting = true;
      })
      .addCase(updateCustomerFromList.fulfilled, (state) => {
        state.formSubmitting = false;
      })
      .addCase(updateCustomerFromList.rejected, (state) => {
        state.formSubmitting = false;
      })
      .addCase(fetchCustomerForEdit.pending, (state) => {
        state.editPrefillLoading = true;
      })
      .addCase(fetchCustomerForEdit.fulfilled, (state) => {
        state.editPrefillLoading = false;
      })
      .addCase(fetchCustomerForEdit.rejected, (state) => {
        state.editPrefillLoading = false;
      });
  },
});

export const {
  setCustomersListSearch,
  setCustomersListLocationFilters,
  setCustomersListSort,
  setCustomersListRange,
  setCustomersListFilters,
  clearCustomersListFilters,
  clearCustomersListDeleteState,
  setCustomersListShowOnlyActive,
  setCustomersListOperatorFilters,
} = customersListSlice.actions;
export default customersListSlice.reducer;
